import { ColumnDef } from "@tanstack/react-table";
import type { Checklist } from "../types/checklist.types";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cleanStandardName } from "@/components/ui/FrameworkCombobox";
import { MoreHorizontal } from "lucide-react";

export const getChecklistColumns = (
  onEdit?: (checklist: Checklist) => void,
  onViewComplete?: (checklist: Checklist) => void,
  onDelete?: (checklist: Checklist) => void,
  onViewHistory?: (checklist: Checklist) => void
): ColumnDef<Checklist, any>[] => [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => <span className="font-medium truncate max-w-[220px] block">{row.original.title}</span>,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => <span className="truncate max-w-[320px] text-muted-foreground">{row.original.description}</span>,
    },
    {
      accessorKey: "standard",
      header: "Framework",
      cell: ({ row }) => {
        const { standard, standards } = row.original;
        const values = Array.isArray(standards) && standards.length
          ? standards
          : (standard ? [standard] : []);

        if (!values.length) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }

        return (
          <div className="flex flex-wrap gap-1">
            {values.map(value => (
              <div key={value} className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="max-w-[200px] truncate text-sm">{
                    (() => {
                      const pretty = cleanStandardName(value);
                      // If pretty name is just the id or a known alias, fallback to id->pretty
                      if (
                        pretty.toLowerCase() === value.toLowerCase()
                      ) {
                        return cleanStandardName(value).charAt(0).toUpperCase() + cleanStandardName(value).slice(1);
                      }
                      return pretty.charAt(0).toUpperCase() + pretty.slice(1);
                    })()
                  }</span>
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
    // {
    //   id: "status",
    //   header: "Status",
    //   cell: ({ row }) => {
    //     const isCompleted = Array.isArray(row.original.completionHistory) && row.original.completionHistory.length > 0 && !!row.original.completionHistory[row.original.completionHistory.length - 1]?.completedAt;
    //     return isCompleted ? (
    //       <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Completed</span>
    //     ) : (
    //       <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">In Progress</span>
    //     );
    //   },
    // },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-input bg-background p-0 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
                <MoreHorizontal className="h-4 w-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onViewComplete?.(row.original)}>
                View / Complete
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit?.(row.original)}>
                Edit
              </DropdownMenuItem>
              {Array.isArray(row.original.completionHistory) && row.original.completionHistory.length > 0 && (
                <DropdownMenuItem onClick={() => onViewHistory?.(row.original)}>
                  View History
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onDelete?.(row.original)}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];
